// Launch Darkly also supports JSON flag values. This implementation doesn't
// support this yet, but we can add it later if we need/want it.
export type LaunchDarklyFlagValue = boolean | number | string

export interface WebFeatureFlags {
  'client-side-report-pdf-export': boolean
  'configure-departments-permission-feature': boolean
  'departments-for-realtime': boolean
  'direct-upload': boolean
  'design-system-overlay': boolean
  'detailed-error-messages': boolean
  'disable-chromium-remapping-hack': boolean
  'export-assets-v2': boolean
  'feature-annotations-appearance': boolean
  'feature-annotations-departments-display': boolean
  'feature-annotations-court-staff': boolean
  'feature-annotations-events': boolean
  'feature-automatic-annotations': string
  'feature-disable-channel-remapping': boolean
  'filter-recordings-list-by-on-the-record-content': boolean
  'persistent-flag-switch': boolean
  'real-time-stt-attribution-approach':
    | 'never-use-software-based-attribution'
    | 'court-system-configures-attribution-per-room'
    | 'always-use-software-based-attribution'
  'realtime-recording-automated-deletion': boolean
  'realtime-recording-on-demand-deletion': boolean
  'recording-vault-activity-log-liveness': boolean
  'recording-vault-support': boolean
  'report-trm-average-upload-time': boolean
  'report-number-of-trm-converted': boolean
  'report-trm-uploads-list': boolean
  'revoke-refresh-token': boolean
  'rth-defined-channel-audio-emission': number // 0 = hybrid recorder emission disabled
  'rth-display-debug': boolean
  'rth-enabled-realtime-hearings': boolean
  'rth-feature-show-infrastructure-status': boolean
  'rth-hybrid-audio-routing': boolean
  'rth-join-courtroom-remotely': boolean
  'rth-schedule-proceedings': boolean
  'standard-stt-delay-seconds': number
  'stt-debug-ui-tooling': boolean
  'stt-licensing-terms': boolean
  // leave this one for unit tests
  'test-boolean-flag': boolean
  'trm-recording-channel-map-strategy': number // See LD for what values map back to
  'courtroom-monitoring-overview-actions-menu': boolean
  'courtroom-monitoring-glance-metrics-filtering': boolean
  'departs-for-realtime-edit-live-or-future-sessions': boolean
  'ordering-reports': boolean
  'use-stripe-payment-intents-api': boolean
  'public-navigation': boolean
  'feature-hearing-summaries': boolean
  'feature-annotations-session-notes': boolean
  'hls-playback-start-stop-hls-loading': boolean
  'recording-session-sealing-api': boolean
  'ftr-forum': boolean
  'video-licencing': boolean
  'departments-before-a-session': boolean
  'rth-upgrader-v2': boolean // Recorder 3.0 feature flag
  'enable-desktop-web-version-checks': boolean
  'feature-case-summaries': boolean
  'realtime-recorder-list-with-pagination': boolean
  'account-details-redesign': boolean
  'case-page-single-scroll-layout': boolean
}

export type FeatureFlagKeyOfType<TValue> = Exclude<
  keyof {
    [K in keyof WebFeatureFlags as WebFeatureFlags[K] extends TValue ? K : never]: never
  },
  symbol | number
>

/* Defined here to avoid circular dependency with real-time-stt.component.ts */
export const DEFAULT_STANDARD_STT_DELAY_SECONDS = 240

export const defaultFlags: WebFeatureFlags = {
  'client-side-report-pdf-export': false,
  'configure-departments-permission-feature': false,
  'departments-for-realtime': false,
  'direct-upload': false,
  'design-system-overlay': false,
  'detailed-error-messages': false,
  'disable-chromium-remapping-hack': false,
  'export-assets-v2': false,
  'feature-annotations-appearance': false,
  'feature-annotations-departments-display': false,
  'feature-annotations-court-staff': false,
  'feature-annotations-events': false,
  'feature-automatic-annotations': 'off',
  'feature-disable-channel-remapping': false,
  'filter-recordings-list-by-on-the-record-content': false,
  'persistent-flag-switch': false,
  'real-time-stt-attribution-approach': 'never-use-software-based-attribution',
  'realtime-recording-automated-deletion': false,
  'realtime-recording-on-demand-deletion': false,
  'recording-vault-activity-log-liveness': false,
  'recording-vault-support': false,
  'report-number-of-trm-converted': false,
  'report-trm-average-upload-time': false,
  'report-trm-uploads-list': false,
  'revoke-refresh-token': false,
  'rth-defined-channel-audio-emission': 0, // 0 = hybrid recorder emission disabled
  'rth-display-debug': false,
  'rth-enabled-realtime-hearings': false,
  'rth-feature-show-infrastructure-status': false,
  'rth-hybrid-audio-routing': false,
  'rth-join-courtroom-remotely': false,
  'rth-schedule-proceedings': false,
  'standard-stt-delay-seconds': DEFAULT_STANDARD_STT_DELAY_SECONDS,
  'stt-debug-ui-tooling': false,
  'stt-licensing-terms': false,
  'test-boolean-flag': false,
  'trm-recording-channel-map-strategy': 0,
  'courtroom-monitoring-overview-actions-menu': false,
  'courtroom-monitoring-glance-metrics-filtering': false,
  'departs-for-realtime-edit-live-or-future-sessions': false,
  'ordering-reports': false,
  'use-stripe-payment-intents-api': true,
  'public-navigation': false,
  'feature-hearing-summaries': false,
  'feature-annotations-session-notes': false,
  'hls-playback-start-stop-hls-loading': false,
  'recording-session-sealing-api': false,
  'ftr-forum': false,
  'video-licencing': false,
  'departments-before-a-session': false,
  'rth-upgrader-v2': false, // Recorder 3.0 feature flag
  'enable-desktop-web-version-checks': false,
  'feature-case-summaries': false,
  'realtime-recorder-list-with-pagination': false,
  'account-details-redesign': false,
  'case-page-single-scroll-layout': false,
}
